import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { RefreshCw, ArrowLeft, Lock, Save, RotateCcw, Upload, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { IMAGES, IMAGE_SLOTS } from "../data/site";
import ContentEditor from "../components/admin/ContentEditor";
import ProjectsManager from "../components/admin/ProjectsManager";
import GalleryManager from "../components/admin/GalleryManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "enquiries", label: "Enquiries" },
  { id: "projects", label: "Projects" },
  { id: "gallery", label: "Gallery" },
  { id: "content", label: "Page Content" },
  { id: "images", label: "Site Images" },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("rr_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [overrides, setOverrides] = useState({});
  const [drafts, setDrafts] = useState({});
  const [uploading, setUploading] = useState({});

  const authHeaders = useCallback((t) => ({ Authorization: `Bearer ${t || token}` }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem("rr_admin_token");
    setToken("");
    setAuthed(false);
  }, []);

  const loadImages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/site-images`);
      setOverrides(res.data || {});
    } catch {
      /* keep defaults */
    }
  }, []);

  const loadData = useCallback(
    async (t) => {
      try {
        const res = await axios.get(`${API}/admin/quotes`, { headers: authHeaders(t) });
        setQuotes(res.data);
        setAuthed(true);
        localStorage.setItem("rr_admin_token", t);
        loadImages();
        return true;
      } catch {
        localStorage.removeItem("rr_admin_token");
        setAuthed(false);
        return false;
      }
    },
    [authHeaders, loadImages]
  );

  useEffect(() => {
    if (token) loadData(token);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      setToken(res.data.token);
      const ok = await loadData(res.data.token);
      if (!ok) setError("Login succeeded but session could not be verified.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  const saveImage = async (slotKey, url) => {
    try {
      await axios.put(`${API}/admin/site-images`, { key: slotKey, url }, { headers: authHeaders() });
      toast.success(url ? "Image updated" : "Image reset to default");
      setDrafts((d) => ({ ...d, [slotKey]: "" }));
      loadImages();
    } catch (e) {
      if (e.response?.status === 401) logout();
      toast.error(e.response?.data?.detail || "Failed to update image");
    }
  };

  const uploadImage = async (slotKey, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [slotKey]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, { headers: authHeaders() });
      await saveImage(slotKey, `/api/uploads/${res.data.path}`);
    } catch (e) {
      if (e.response?.status === 401) logout();
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [slotKey]: false }));
    }
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6 text-[#F5F5F5]">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm border border-white/10 bg-[#0A0A0A] p-8"
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center rounded-md bg-white px-2 py-1">
              <img src="/logo.png" alt="Renuka Roofing Structurals" className="h-9 w-auto" />
            </span>
            <Link to="/" data-testid="admin-back-link" className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#3377FF]">
              Back to site
            </Link>
          </div>
          <h1 className="mt-6 font-display text-2xl font-bold tracking-tight">Admin Login</h1>
          <p className="mt-2 flex items-center gap-2 text-sm text-[#A3A3A3]">
            <Lock size={14} className="text-[#0055FF]" /> Sign in to manage your website
          </p>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="mt-6 w-full border border-white/15 bg-[#050505] px-4 py-3 text-sm outline-none focus:border-[#0055FF]"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="mt-3 w-full border border-white/15 bg-[#050505] px-4 py-3 text-sm outline-none focus:border-[#0055FF]"
          />
          {error && (
            <p data-testid="admin-error" className="mt-3 text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-5 w-full bg-[#0055FF] py-3 font-display text-sm font-bold uppercase tracking-widest transition-colors hover:bg-[#3377FF] disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-10 text-[#F5F5F5] md:px-12">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="admin-title">
              Website Manager
            </h1>
            <p className="mt-1 text-xs uppercase tracking-[0.25em] text-white/40">Renuka Roofing Structurals</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-back-link"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:text-[#3377FF]"
            >
              <ArrowLeft size={14} /> View site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors hover:border-red-400 hover:text-red-400"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-px" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-[12px] font-bold uppercase tracking-[0.15em] transition-colors ${
                tab === t.id
                  ? "border-b-2 border-[#0055FF] text-white"
                  : "text-[#A3A3A3] hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#A3A3A3]" data-testid="admin-count">
                {quotes.length} enquir{quotes.length === 1 ? "y" : "ies"}
              </p>
              <button
                data-testid="admin-refresh-button"
                onClick={() => loadData(token)}
                className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors hover:border-[#0055FF]"
              >
                <RefreshCw size={13} /> Refresh
              </button>
            </div>
            <div className="mt-4 space-y-3" data-testid="admin-quotes-list">
              {quotes.length === 0 && (
                <p className="border border-white/10 bg-[#0A0A0A] p-8 text-center text-sm text-[#A3A3A3]">
                  No enquiries yet.
                </p>
              )}
              {quotes.map((q, i) => (
                <motion.div
                  key={q.id}
                  data-testid={`admin-quote-${i}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="border border-white/10 bg-[#0A0A0A] p-5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-display text-lg font-semibold">{q.name}</p>
                    <span className="text-xs text-white/40">{new Date(q.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-1 text-sm text-[#3377FF]">{q.phone}</p>
                  {q.email && <p className="text-sm text-[#A3A3A3]">{q.email}</p>}
                  {q.service && (
                    <span className="mt-2 inline-block bg-[#0055FF]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#3377FF]">
                      {q.service}
                    </span>
                  )}
                  {q.message && <p className="mt-2 text-sm leading-relaxed text-[#A3A3A3]">{q.message}</p>}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {tab === "projects" && <ProjectsManager token={token} onUnauthorized={logout} />}
        {tab === "gallery" && <GalleryManager token={token} onUnauthorized={logout} />}
        {tab === "content" && <ContentEditor token={token} onUnauthorized={logout} />}

        {tab === "images" && (
          <div className="mt-8" data-testid="admin-images-section">
            <p className="max-w-2xl text-sm leading-relaxed text-[#A3A3A3]">
              Replace any photo on the website: click Upload to pick a photo from your phone or computer (JPG, PNG or
              WebP, up to 10MB), or paste a public image link and Save. Use Reset to return to the default demo image.
              Changes appear on the site on next page load.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {IMAGE_SLOTS.map((slot) => {
                const current = overrides[slot.key] || IMAGES[slot.key];
                return (
                  <div key={slot.key} data-testid={`image-slot-${slot.key}`} className="border border-white/10 bg-[#0A0A0A] p-4">
                    <div className="relative overflow-hidden">
                      <img src={current} alt={slot.label} className="aspect-[16/9] w-full object-cover" />
                      {overrides[slot.key] && (
                        <span className="absolute left-2 top-2 bg-[#0055FF] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white">
                          Custom
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-sm font-semibold">{slot.label}</p>
                    <input
                      data-testid={`image-input-${slot.key}`}
                      value={drafts[slot.key] ?? ""}
                      onChange={(e) => setDrafts((d) => ({ ...d, [slot.key]: e.target.value }))}
                      placeholder="https:// new image link"
                      className="mt-3 w-full border border-white/15 bg-[#050505] px-3 py-2.5 text-xs outline-none focus:border-[#0055FF]"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <label
                        data-testid={`image-upload-${slot.key}`}
                        className={`flex cursor-pointer items-center gap-1.5 bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-[#050505] transition-colors hover:bg-white/80 ${
                          uploading[slot.key] ? "pointer-events-none opacity-50" : ""
                        }`}
                      >
                        <Upload size={12} /> {uploading[slot.key] ? "Uploading..." : "Upload"}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => {
                            uploadImage(slot.key, e.target.files?.[0]);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      <button
                        data-testid={`image-save-${slot.key}`}
                        onClick={() => saveImage(slot.key, (drafts[slot.key] || "").trim())}
                        disabled={!(drafts[slot.key] || "").trim()}
                        className="flex items-center gap-1.5 bg-[#0055FF] px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors hover:bg-[#3377FF] disabled:opacity-40"
                      >
                        <Save size={12} /> Save
                      </button>
                      {overrides[slot.key] && (
                        <button
                          data-testid={`image-reset-${slot.key}`}
                          onClick={() => saveImage(slot.key, "")}
                          className="flex items-center gap-1.5 border border-white/15 px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors hover:border-white/40"
                        >
                          <RotateCcw size={12} /> Reset
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
